import React, {FC, useEffect, useState} from 'react';
import {FilterIcon} from '@epic-ui/icons';
import Box from '@material-ui/core/Box';
import {getLocale} from '@epic-core/common/dist';
import Typography from '@material-ui/core/Typography';
import {useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import {Widget} from '../../../../common/widget';
import IconButton from '../../../../common/IconButton';
import WidgetTooltip from '../components/WidgetTooltip';
import SummaryItemLabel from '../components/SummaryItemLabel';
import {PDFPictureTrap} from '../export/PDFExportProvider';
import ChartOverview from '../../../../chart-overview/ChartOverview';
import WidgetDownloadButton from '../components/WidgetDownloadButton';
import WidgetGameSelect from '../../../../game-select/WidgetGameSelect';
import LifetimePlaysBarChart from '../components/chart/LifetimePlaysBarChart';

import {WidgetProps} from '../types';
import {Status, TimeScale} from '../../../../../types';

import {ChartWrapper, OptionWrapper} from '../styles';

import {numberFormatter} from '../../../../../utils/FNUtils';
import {prepareLifetimePlaysExportData} from '../exportUtils';
import useLifetimePlaysData from './useLifetimePlaysData';
import useLocalization from '../../../../../hooks/useLocalization';
import useWidgetSelectedGames from '../hooks/useWidgetSelectedGames';
import useOrdinalNumberFormatter from '../hooks/useOrdinalNumberFormatter';
import {LEGENDS_LIMIT} from '../constants';

const TIME_SCALE = TimeScale.DAILY;

const CODES = {
    title: 'epic.affiliate.fortnite_games.lifetime_plays',
    tooltip: 'epic.affiliate.fortnite_games.lifetime_plays.tooltip',
    selectedVisits: 'epic.affiliate.fortnite_games.lifetime_plays.selected_visits',
    mostVisited: 'epic.affiliate.fortnite_games.lifetime_plays.most_visited',
    tooltipSelectedVisits: 'epic.affiliate.fortnite_games.lifetime_plays.overview.selected_visits',
    tooltipMostVisited: 'epic.affiliate.fortnite_games.lifetime_plays.overview.most_visited'
};

export type TotalLifetimePlaysWidgetProps = Omit<WidgetProps, 'getMessage'>;

const TotalLifetimePlaysWidget: FC<TotalLifetimePlaysWidgetProps> = ({
    startDate,
    endDate,
    selectedGames: selectedGamesProps,
    gamesList,
    keysMap,
    gamesLoading
}: TotalLifetimePlaysWidgetProps) => {
    const theme = useTheme();
    const isFmdBreakpoint = useMediaQuery(theme.breakpoints.up('fmd'));
    const formatOrdinal = useOrdinalNumberFormatter();
    const getMessage = useLocalization();

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [selectedGames, handleSelectGames] = useWidgetSelectedGames(selectedGamesProps);

    const {data, loading, error, reload} = useLifetimePlaysData({
        startDate,
        endDate,
        timeScale: TIME_SCALE,
        selectedGames
    });

    const formatNumber = numberFormatter(getLocale());

    const {empty: emptyDataKeys, overallVisits, mostVisited, list = []} = data[TIME_SCALE] || {};
    const chartData = list.sort((itemA, itemB) => {
        const aValue = itemA[itemA.creativeIslandCode] || 0;
        const bValue = itemB[itemB.creativeIslandCode] || 0;

        return bValue - aValue;
    });

    // Handlers
    const handleMobileMenuClick = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [isFmdBreakpoint]);

    return (
        <PDFPictureTrap id={CODES.title} index={4}>
            <div>
                <Widget
                    height={647}
                    header={getMessage(CODES.title)}
                    padding="15px"
                    tooltip={<WidgetTooltip>{getMessage(CODES.tooltip)}</WidgetTooltip>}
                    status={error ? Status.ERROR : undefined}
                    message={getMessage(error) || error}
                    overflow="visible"
                    retry={reload}
                    actions={
                        <>
                            {isFmdBreakpoint ? (
                                <>
                                    <Box mx={1.5} width={178}>
                                        <WidgetGameSelect
                                            options={gamesList}
                                            value={selectedGames}
                                            onChange={handleSelectGames}
                                        />
                                    </Box>
                                </>
                            ) : (
                                <IconButton
                                    onClick={handleMobileMenuClick}
                                    isOpen={isMobileMenuOpen}>
                                    <FilterIcon />
                                </IconButton>
                            )}
                            <WidgetDownloadButton
                                areaId={CODES.title}
                                fileName={getMessage(CODES.title)}
                                data={chartData}
                                prepareCSVData={prepareLifetimePlaysExportData(
                                    keysMap,
                                    formatOrdinal
                                )}
                                disabled={loading || gamesLoading || !!error}
                            />
                        </>
                    }
                    options={
                        isMobileMenuOpen &&
                        !isFmdBreakpoint && (
                            <OptionWrapper>
                                <WidgetGameSelect
                                    options={gamesList}
                                    value={selectedGames}
                                    onChange={handleSelectGames}
                                />
                            </OptionWrapper>
                        )
                    }>
                    <ChartWrapper>
                        <LifetimePlaysBarChart
                            selectedGames={selectedGames}
                            height={455}
                            data={chartData}
                            limit={LEGENDS_LIMIT}
                            loading={loading || gamesLoading}
                            keysMap={keysMap}
                            emptyDataKeys={emptyDataKeys}
                        />
                        <ChartOverview
                            gridTemplateColumns="repeat(2, 1fr)"
                            gridTemplateColumnsSM="repeat(2, 1fr)">
                            <ChartOverview.Item
                                loading={gamesLoading || loading}
                                label={
                                    <SummaryItemLabel
                                        tooltip={getMessage(CODES.tooltipSelectedVisits)}>
                                        {getMessage(CODES.selectedVisits)}
                                    </SummaryItemLabel>
                                }>
                                {formatNumber(overallVisits)}
                            </ChartOverview.Item>
                            <ChartOverview.Item
                                loading={gamesLoading || loading}
                                label={
                                    <SummaryItemLabel
                                        tooltip={getMessage(CODES.tooltipMostVisited)}>
                                        {getMessage(CODES.mostVisited)}
                                    </SummaryItemLabel>
                                }
                                secondaryText={
                                    <Typography variant="caption" color="textSecondary">
                                        {String(keysMap[mostVisited.creativeIslandCode] || '-')}
                                    </Typography>
                                }>
                                {formatNumber(mostVisited.value)}
                            </ChartOverview.Item>
                        </ChartOverview>
                    </ChartWrapper>
                </Widget>
            </div>
        </PDFPictureTrap>
    );
};

export default TotalLifetimePlaysWidget;
